<script setup lang="ts">
import type { ICellRendererParams } from "ag-grid-community";
import { computed } from "vue";

import { copyAndNotify } from "~/components/notifications";
import TaskClickWrapper from "~/components/visualization/components/TaskClickWrapper.vue";
import { EditorMode } from "~/shared/enums";
import type { VisualizationItemTask } from "~/shared/types";
import { useDataStore } from "~/stores";
import { useAsyncTask } from "~/utils/entityIncrementalLoading";
import { prettifyOrder } from "~/utils/orderManager";

const props = defineProps<{
  params: ICellRendererParams<VisualizationItemTask>;
}>();

const dataStore = useDataStore();
const { task } = useAsyncTask(computed(() => props.params?.data?.duid));

const pretty = computed(() => (task.value ? prettifyOrder(task.value.order) : ""));
const isDartboardTopTask = computed(() => {
  if (!task.value) {
    return false;
  }
  const dartboard = dataStore.getDartboardByDuid(task.value.dartboardDuid);
  return dartboard?.topTaskOrder === task.value.order;
});

const copy = () => {
  if (!task.value) {
    return;
  }
  copyAndNotify("Order", task.value.order);
};
</script>

<template>
  <TaskClickWrapper v-if="task" :task="task" :editor-mode="EditorMode.LIST">
    <template #default="args">
      <div
        v-if="!params.data?.absentee"
        class="flex size-full items-center overflow-hidden"
        v-bind="args"
        @click="copy"
        @keydown.enter="copy">
        <div
          class="w-full truncate p-1 font-mono text-[10px] tracking-tighter"
          :class="{ 'text-primary-base': isDartboardTopTask }">
          {{ pretty }}
        </div>
      </div>
    </template>
  </TaskClickWrapper>
</template>
